import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import AddPayComponent from './AddPayComponent';
import EditPayComponent from './EditPayComponent';
import axios from 'axios';

const PayComponents = () => {
  const [payComponents, setPayComponents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('All');
  const [calculationFilter, setCalculationFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [sortBy, setSortBy] = useState('name');
  const [viewMode, setViewMode] = useState('list');
  const [userPermissions, setUserPermissions] = useState({
    can_view: false,
    can_add: false,
    can_edit: false,
    can_delete: false,
    can_export: false,
    loaded: false
  });

  useAuth();
  const navigate = useNavigate();

  const API_BASE = process.env.REACT_APP_API_BASE;

  // Fetch user permissions
  const fetchUserPermissions = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setUserPermissions({
          can_view: false,
          can_add: false,
          can_edit: false,
          can_delete: false,
          can_export: false,
          loaded: true
        });
        return;
      }

      const response = await axios.get(`${API_BASE}/get_user_permissions.php`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success && response.data.permissions) {
        const payComponentsPermissions = response.data.permissions.find(
          p => p.module_name === 'PayComponents' || p.module_name === 'Payroll'
        );

        if (payComponentsPermissions) {
          setUserPermissions({
            can_view: Boolean(payComponentsPermissions.can_view),
            can_add: Boolean(payComponentsPermissions.can_add),
            can_edit: Boolean(payComponentsPermissions.can_edit),
            can_delete: Boolean(payComponentsPermissions.can_delete),
            can_export: Boolean(payComponentsPermissions.can_export),
            loaded: true
          });
        } else {
          setUserPermissions({
            can_view: false,
            can_add: false,
            can_edit: false,
            can_delete: false,
            can_export: false,
            loaded: true
          });
        }
      } else {
        setUserPermissions({
          can_view: false,
          can_add: false,
          can_edit: false,
          can_delete: false,
          can_export: false,
          loaded: true
        });
      }
    } catch (error) {
      console.error('Error fetching permissions:', error);
      setUserPermissions({
        can_view: false,
        can_add: false,
        can_edit: false,
        can_delete: false,
        can_export: false,
        loaded: true
      });
    }
  }, [API_BASE]);

  // Fetch pay components
  const fetchPayComponents = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('authToken');
      if (!token) {
        setError('Authentication token not found');
        setLoading(false);
        return;
      }

      const response = await axios.get(`${API_BASE}/pay_components.php`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('API Response:', response.data); // Debug log

      if (response.data.success) {
        setPayComponents(response.data.pay_components || []);
      } else {
        setError(response.data.error || 'Failed to fetch pay components');
      }
    } catch (error) {
      console.error('Error fetching pay components:', error);
      setError(error.response?.data?.error || error.message || 'Failed to fetch pay components');
    } finally {
      setLoading(false);
    }
  }, [API_BASE]);

  // Initialize data on component mount
  useEffect(() => {
    const initializeData = async () => {
      await fetchUserPermissions();
      await fetchPayComponents();
    };

    initializeData();
  }, [fetchUserPermissions, fetchPayComponents]);

  const handleDelete = async (componentId) => {
    if (!userPermissions.can_delete) {
      alert('You do not have permission to delete pay components');
      return;
    }

    if (window.confirm('Are you sure you want to delete this pay component?')) {
      try {
        const token = localStorage.getItem('authToken');
        const response = await axios.post(`${API_BASE}/delete_pay_component.php`,
          { component_id: componentId },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data.success) {
          fetchPayComponents();
          alert('Pay component deleted successfully');
        } else {
          setError(response.data.error || 'Failed to delete pay component');
        }
      } catch (error) {
        setError(error.response?.data?.error || 'Failed to delete pay component');
        console.error('Error deleting pay component:', error);
      }
    }
  };

  const handleEdit = (component) => {
    if (!userPermissions.can_edit) {
      alert('You do not have permission to edit pay components');
      return;
    }
    setSelectedComponent(component);
    setShowEditModal(true);
  };

  const handleToggleStatus = async (componentId, currentStatus) => {
    if (!userPermissions.can_edit) {
      alert('You do not have permission to update pay components');
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.post(`${API_BASE}/toggle_pay_component_status.php`,
        {
          component_id: componentId,
          current_status: currentStatus
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        fetchPayComponents();
      } else {
        setError(response.data.error || 'Failed to update status');
      }
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to update status');
      console.error('Error updating status:', error);
    }
  };

  const handleViewDetails = (component) => {
    if (!userPermissions.can_view) {
      alert('You do not have permission to view pay component details');
      return;
    }

    let formattedValue = component.value;
    if (component.calculation_type === 'Fixed') {
      if (component.component_name.includes('Ceiling') || component.value.includes('.')) {
        formattedValue = `₹${parseFloat(component.value).toLocaleString('en-IN')}`;
      } else {
        formattedValue = `₹${component.value}`;
      }
    } else if (component.calculation_type === 'Percentage') {
      formattedValue = `${component.value}%`;
    }

    alert(`Pay Component Details:\n\nName: ${component.component_name}\nType: ${component.component_type}\nCalculation: ${component.calculation_type}\nValue: ${formattedValue}\nStatus: ${component.is_active ? 'Active' : 'Inactive'}`);
  };

  const formatValue = (component) => {
    if (component.calculation_type === 'Fixed') {
      if (component.component_name.includes('Ceiling') || component.value.includes('.')) {
        return `₹${parseFloat(component.value).toLocaleString('en-IN')}`;
      }
      return `₹${component.value}`;
    } else if (component.calculation_type === 'Percentage') {
      return `${component.value}%`;
    } else {
      return component.value;
    }
  };

  // Navigation handlers
  const goToDashboard = () => {
    navigate('/dashboard');
  };

  const goToHome = () => {
    navigate('/');
  };

  const goToAdminPanel = () => {
    navigate('/admin-panel');
  };

  const goToEmployees = () => {
    navigate('/employees');
  };

  // Filter pay components
  const filteredComponents = payComponents.filter(component => {
    const matchesSearch =
      component.component_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      component.component_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      component.calculation_type?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesType = typeFilter === 'All' || component.component_type === typeFilter;
    const matchesCalculation = calculationFilter === 'All' || component.calculation_type === calculationFilter;
    const matchesStatus = statusFilter === 'All' ||
      (statusFilter === 'Active' && component.is_active) ||
      (statusFilter === 'Inactive' && !component.is_active);

    return matchesSearch && matchesType && matchesCalculation && matchesStatus;
  });

  // Sort pay components
  const sortedComponents = [...filteredComponents].sort((a, b) => {
    switch (sortBy) {
      case 'name':
        return a.component_name.localeCompare(b.component_name);
      case 'type':
        return a.component_type.localeCompare(b.component_type);
      case 'calculation':
        return a.calculation_type.localeCompare(b.calculation_type);
      case 'date':
        return b.component_id - a.component_id;
      default:
        return 0;
    }
  });

  // Get component icon
  const getComponentIcon = (componentType) => {
    if (componentType === 'Earning') return 'bi-plus-circle';
    if (componentType === 'Deduction') return 'bi-dash-circle';
    if (componentType === 'CTC') return 'bi-cash-stack';
    if (componentType === 'General') return 'bi-gear';
    return 'bi-question-circle';
  };

  // Get calculation icon
  const getCalculationIcon = (calculationType) => {
    switch (calculationType) {
      case 'Fixed': return 'bi-currency-rupee';
      case 'Percentage': return 'bi-percent';
      case 'Formula': return 'bi-calculator';
      default: return 'bi-question-circle';
    }
  };

  // Get calculation display text
  const getCalculationDisplay = (calculationType) => {
    switch (calculationType) {
      case 'Fixed': return 'Fixed (₹)';
      case 'Percentage': return 'Percentage (%)';
      case 'Formula': return 'Formula';
      default: return calculationType;
    }
  };

  // Get component type badge color
  const getComponentTypeColor = (componentType) => {
    switch (componentType) {
      case 'Earning': return 'success';
      case 'Deduction': return 'danger';
      case 'CTC': return 'info';
      case 'General': return 'secondary';
      default: return 'secondary';
    }
  };

  // Get calculation type badge color
  const getCalculationTypeColor = (calculationType) => {
    switch (calculationType) {
      case 'Fixed': return 'primary';
      case 'Percentage': return 'info';
      case 'Formula': return 'warning';
      default: return 'secondary';
    }
  };

  // Check if component is a required default component
  const isRequiredComponent = (componentName) => {
    const requiredNames = [
      'PF_Ceiling', 'PF_Percentage', 'PF_Employer_Percentage',
      'ESI_Ceiling', 'ESI_Percentage', 'ESI_Employer_Percentage',
      'Effective_Days', 'Present_Days', 'FH', 'LOP', 'LayOff', 'PL'
    ];
    return requiredNames.includes(componentName);
  };

  // Permission check functions
  const canAddComponent = () => userPermissions.can_add;
  const canEditComponent = (component) => {
    if (!userPermissions.can_edit) return false;
    // Don't allow editing required components
    if (isRequiredComponent(component.component_name)) return false;
    return true;
  };
  const canDeleteComponent = (component) => {
    if (!userPermissions.can_delete) return false;
    // Don't allow deleting required components
    if (isRequiredComponent(component.component_name)) return false;
    return true;
  };
  const canViewComponents = () => userPermissions.can_view;

  // Show loading while permissions are being fetched
  if (loading || !userPermissions.loaded) {
    return (
      <div className="d-flex justify-content-center align-items-center youtube-page-container" style={{ height: '50vh' }}>
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2 text-muted">Loading pay components...</p>
        </div>
      </div>
    );
  }

  // If user doesn't have view permission, show access denied
  if (!canViewComponents()) {
    return (
      <div className="container-fluid py-4 youtube-page-container">
        <div className="row">
          <div className="col-12">
            <div className="card shadow-sm border-0">
              <div className="card-body text-center py-5">
                <i className="bi bi-shield-exclamation display-1 text-warning"></i>
                <h3 className="mt-3 text-warning">Access Denied</h3>
                <p className="text-muted">You do not have permission to view pay components.</p>
                <button
                  className="btn btn-primary mt-3"
                  onClick={goToDashboard}
                >
                  <i className="bi bi-speedometer2 me-2"></i>
                  Back to Dashboard
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4 youtube-page-container">
      {/* Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card shadow-sm border-0">
            <div className="card-body py-4">
              <div className="row align-items-center">
                <div className="col-md-6">
                  <div className="d-flex align-items-center">
                    <div className="btn-group me-3" role="group">
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        onClick={goToHome}
                        title="Go to Home"
                      >
                        <i className="bi bi-house"></i>
                      </button>
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        onClick={goToDashboard}
                        title="Go to Dashboard"
                      >
                        <i className="bi bi-speedometer2"></i>
                      </button>
                    </div>
                    <div>
                      <h2 className="h4 mb-1 fw-bold text-dark">
                        <i className="bi bi-currency-rupee me-2 text-primary"></i>
                        Pay Components Management
                        {!canAddComponent() && !userPermissions.can_edit && !userPermissions.can_delete && (
                          <span className="badge bg-warning ms-2">View Only</span>
                        )}
                      </h2>
                      <p className="text-muted mb-0">
                        Manage salary components like earnings, deductions, and CTC in Indian Rupees (₹)
                      </p>
                    </div>
                  </div>
                </div>
                <div className="col-md-6 text-end">
                  <div className="d-flex justify-content-end align-items-center">
                    <div className="btn-group me-2" role="group">
                      <button
                        className={`btn btn-outline-primary ${viewMode === 'list' ? 'active' : ''}`}
                        onClick={() => setViewMode('list')}
                        title="List View"
                      >
                        <i className="bi bi-list-ul"></i>
                      </button>
                      <button
                        className={`btn btn-outline-primary ${viewMode === 'grid' ? 'active' : ''}`}
                        onClick={() => setViewMode('grid')}
                        title="Grid View"
                      >
                        <i className="bi bi-grid-3x3-gap"></i>
                      </button>
                    </div>
                    {canAddComponent() && (
                      <button
                        className="btn btn-primary btn-lg"
                        onClick={() => setShowAddModal(true)}
                      >
                        <i className="bi bi-plus-circle me-2"></i>
                        Add Pay Component
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Navigation */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card bg-light border-0">
            <div className="card-body py-3">
              <div className="row text-center">
                <div className="col-md-2 mb-2 mb-md-0">
                  <button
                    className="btn btn-outline-primary w-100 d-flex align-items-center justify-content-center"
                    onClick={goToHome}
                  >
                    <i className="bi bi-house me-2"></i>
                    Home
                  </button>
                </div>
                <div className="col-md-2 mb-2 mb-md-0">
                  <button
                    className="btn btn-outline-success w-100 d-flex align-items-center justify-content-center"
                    onClick={goToDashboard}
                  >
                    <i className="bi bi-speedometer2 me-2"></i>
                    Dashboard
                  </button>
                </div>
                <div className="col-md-2 mb-2 mb-md-0">
                  <button
                    className="btn btn-outline-info w-100 d-flex align-items-center justify-content-center"
                    onClick={goToEmployees}
                  >
                    <i className="bi bi-people me-2"></i>
                    Employees
                  </button>
                </div>
                <div className="col-md-2 mb-2 mb-md-0">
                  <button
                    className="btn btn-primary w-100 d-flex align-items-center justify-content-center"
                    disabled
                  >
                    <i className="bi bi-currency-rupee me-2"></i>
                    Pay Components
                  </button>
                </div>
                <div className="col-md-2 mb-2 mb-md-0">
                  <button
                    className="btn btn-outline-warning w-100 d-flex align-items-center justify-content-center"
                    onClick={goToAdminPanel}
                  >
                    <i className="bi bi-gear me-2"></i>
                    Admin Panel
                  </button>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card shadow-sm border-0">
            <div className="card-body">
              <div className="row g-3 align-items-center">
                <div className="col-md-3">
                  <div className="input-group">
                    <span className="input-group-text bg-light border-end-0">
                      <i className="bi bi-search text-muted"></i>
                    </span>
                    <input
                      type="text"
                      className="form-control border-start-0"
                      placeholder="Search components..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                      <button
                        className="btn btn-outline-secondary border-start-0"
                        onClick={() => setSearchTerm('')}
                      >
                        <i className="bi bi-x"></i>
                      </button>
                    )}
                  </div>
                </div>
                <div className="col-md-2">
                  <select
                    className="form-select"
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                  >
                    <option value="All">All Types</option>
                    <option value="General">General</option>
                    <option value="Earning">Earning</option>
                    <option value="Deduction">Deduction</option>
                    <option value="CTC">CTC</option>
                  </select>
                </div>
                <div className="col-md-2">
                  <select
                    className="form-select"
                    value={calculationFilter}
                    onChange={(e) => setCalculationFilter(e.target.value)}
                  >
                    <option value="All">All Calculations</option>
                    <option value="Fixed">Fixed (₹)</option>
                    <option value="Percentage">Percentage (%)</option>
                    <option value="Formula">Formula</option>
                  </select>
                </div>
                <div className="col-md-2">
                  <select
                    className="form-select"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="All">All Status</option>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>
                <div className="col-md-2">
                  <select
                    className="form-select"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                  >
                    <option value="name">Sort by Name</option>
                    <option value="type">Sort by Type</option>
                    <option value="calculation">Sort by Calculation</option>
                    <option value="date">Sort by Date Added</option>
                  </select>
                </div>
                <div className="col-md-1 text-end">
                  <button
                    className="btn btn-outline-secondary"
                    onClick={fetchPayComponents}
                    title="Refresh"
                  >
                    <i className="bi bi-arrow-clockwise"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="alert alert-danger d-flex align-items-center" role="alert">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              <div>{error}</div>
              <button
                type="button"
                className="btn-close ms-auto"
                onClick={() => setError('')}
              ></button>
            </div>
          </div>
        </div>
      )}

      {/* Pay Components Display */}
      {viewMode === 'list' ? (
        <div className="row">
          <div className="col-12">
            <div className="card shadow-sm border-0">
              <div className="card-body p-0">
                {sortedComponents.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="bi bi-currency-rupee display-1 text-muted"></i>
                    <h4 className="mt-3 text-muted">No pay components found</h4>
                    <p className="text-muted">
                      {payComponents.length === 0
                        ? "Get started by adding your first pay component."
                        : "No components match your search criteria."
                      }
                    </p>
                    {payComponents.length === 0 && canAddComponent() && (
                      <button
                        className="btn btn-primary mt-2"
                        onClick={() => setShowAddModal(true)}
                      >
                        <i className="bi bi-plus-circle me-2"></i>
                        Add First Component
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                      <thead className="bg-light">
                        <tr>
                          <th className="ps-4">Component Name</th>
                          <th>Type</th>
                          <th>Calculation Type</th>
                          <th>Value/Formula</th>
                          <th>Status</th>
                          <th className="text-end pe-4">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedComponents.map((component) => (
                          <tr key={component.component_id} className="component-row">
                            <td className="ps-4">
                              <div className="d-flex align-items-center">
                                <div className={`component-icon bg-${getComponentTypeColor(component.component_type)} text-white rounded-circle d-flex align-items-center justify-content-center me-3`}
                                  style={{ width: '40px', height: '40px' }}
                                >
                                  <i className={`bi ${getComponentIcon(component.component_type)}`}></i>
                                </div>
                                <div>
                                  <div className="fw-semibold">
                                    {component.component_name}
                                    {isRequiredComponent(component.component_name) && (
                                      <span className="badge bg-warning ms-2">Required</span>
                                    )}
                                  </div>
                                  <small className="text-muted">ID: {component.component_id}</small>
                                </div>
                              </div>
                            </td>
                            <td>
                              <span className={`badge bg-${getComponentTypeColor(component.component_type)}`}>
                                {component.component_type}
                              </span>
                            </td>
                            <td>
                              <span className={`badge bg-${getCalculationTypeColor(component.calculation_type)}`}>
                                <i className={`bi ${getCalculationIcon(component.calculation_type)} me-1`}></i>
                                {getCalculationDisplay(component.calculation_type)}
                              </span>
                            </td>
                            <td>
                              <div className="text-truncate value-display" style={{ maxWidth: '200px' }} title={component.value}>
                                <small>
                                  <i className={`bi ${getCalculationIcon(component.calculation_type)} me-1`}></i>
                                  {formatValue(component)}
                                </small>
                              </div>
                            </td>
                            <td>
                              <div className="form-check form-switch">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  role="switch"
                                  id={`status-${component.component_id}`}
                                  checked={component.is_active}
                                  onChange={() => handleToggleStatus(component.component_id, component.is_active)}
                                  disabled={!userPermissions.can_edit || isRequiredComponent(component.component_name)}
                                />
                                <label className="form-check-label" htmlFor={`status-${component.component_id}`}>
                                  {component.is_active ? 'Active' : 'Inactive'}
                                </label>
                              </div>
                            </td>
                            <td className="text-end pe-4">
                              <div className="btn-group" role="group">
                                <button
                                  className="btn btn-outline-info btn-sm"
                                  onClick={() => handleViewDetails(component)}
                                  title="View Details"
                                >
                                  <i className="bi bi-eye"></i>
                                </button>
                                {canEditComponent(component) && (
                                  <button
                                    className="btn btn-outline-primary btn-sm"
                                    onClick={() => handleEdit(component)}
                                    title="Edit"
                                  >
                                    <i className="bi bi-pencil"></i>
                                  </button>
                                )}
                                {canDeleteComponent(component) && (
                                  <button
                                    className="btn btn-outline-danger btn-sm"
                                    onClick={() => handleDelete(component.component_id)}
                                    title="Delete"
                                  >
                                    <i className="bi bi-trash"></i>
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="row">
          {sortedComponents.length === 0 ? (
            <div className="col-12">
              <div className="text-center py-5">
                <i className="bi bi-currency-rupee display-1 text-muted"></i>
                <h4 className="mt-3 text-muted">No pay components found</h4>
                <p className="text-muted">
                  {payComponents.length === 0
                    ? "Get started by adding your first pay component."
                    : "No components match your search criteria."
                  }
                </p>
                {payComponents.length === 0 && canAddComponent() && (
                  <button
                    className="btn btn-primary mt-2"
                    onClick={() => setShowAddModal(true)}
                  >
                    <i className="bi bi-plus-circle me-2"></i>
                    Add First Component
                  </button>
                )}
              </div>
            </div>
          ) : (
            sortedComponents.map((component) => (
              <div key={component.component_id} className="col-md-6 col-lg-4 col-xl-3 mb-4">
                <div className="card component-card shadow-sm border-0 h-100">
                  <div className="card-body text-center">
                    <div className={`component-icon-lg bg-${getComponentTypeColor(component.component_type)} text-white rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3`}
                      style={{ width: '80px', height: '80px' }}
                    >
                      <i className={`bi ${getComponentIcon(component.component_type)} display-6`}></i>
                    </div>

                    <h5 className="card-title fw-bold mb-1">
                      {component.component_name}
                      {isRequiredComponent(component.component_name) && (
                        <span className="badge bg-warning ms-1">Required</span>
                      )}
                    </h5>
                    <p className="text-muted small mb-2">ID: {component.component_id}</p>

                    <div className="mb-3">
                      <span className={`badge bg-${getComponentTypeColor(component.component_type)} me-1`}>
                        {component.component_type}
                      </span>
                      <span className={`badge bg-${getCalculationTypeColor(component.calculation_type)}`}>
                        <i className={`bi ${getCalculationIcon(component.calculation_type)} me-1`}></i>
                        {getCalculationDisplay(component.calculation_type)}
                      </span>
                    </div>

                    <div className="card bg-light p-3 mb-3">
                      <small className="text-muted">
                        <strong>
                          <i className={`bi ${getCalculationIcon(component.calculation_type)} me-1`}></i>
                          Value/Formula:
                        </strong>
                        <div className="text-truncate mt-1" title={component.value}>
                          {formatValue(component)}
                        </div>
                      </small>
                    </div>

                    <div className="mb-3">
                      <div className="form-check form-switch d-inline-block">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          id={`grid-status-${component.component_id}`}
                          checked={component.is_active}
                          onChange={() => handleToggleStatus(component.component_id, component.is_active)}
                          disabled={!userPermissions.can_edit || isRequiredComponent(component.component_name)}
                        />
                        <label className="form-check-label" htmlFor={`grid-status-${component.component_id}`}>
                          {component.is_active ? 'Active' : 'Inactive'}
                        </label>
                      </div>
                    </div>

                    <div className="btn-group w-100" role="group">
                      <button
                        className="btn btn-outline-info btn-sm"
                        onClick={() => handleViewDetails(component)}
                        title="View Details"
                      >
                        <i className="bi bi-eye"></i>
                      </button>
                      {canEditComponent(component) && (
                        <button
                          className="btn btn-outline-primary btn-sm"
                          onClick={() => handleEdit(component)}
                          title="Edit"
                        >
                          <i className="bi bi-pencil"></i>
                        </button>
                      )}
                      {canDeleteComponent(component) && (
                        <button
                          className="btn btn-outline-danger btn-sm"
                          onClick={() => handleDelete(component.component_id)}
                          title="Delete"
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Stats Footer */}
      <div className="row mt-4">
        <div className="col-12">
          <div className="card bg-light border-0">
            <div className="card-body py-3">
              <div className="row text-center">
                <div className="col-md-2">
                  <div className="fw-bold text-primary fs-4">{payComponents.length}</div>
                  <small className="text-muted">Total Components</small>
                </div>
                <div className="col-md-2">
                  <div className="fw-bold text-secondary fs-4">
                    {payComponents.filter(c => c.component_type === 'General').length}
                  </div>
                  <small className="text-muted">General</small>
                </div>
                <div className="col-md-2">
                  <div className="fw-bold text-success fs-4">
                    {payComponents.filter(c => c.component_type === 'Earning').length}
                  </div>
                  <small className="text-muted">Earnings</small>
                </div>
                <div className="col-md-2">
                  <div className="fw-bold text-danger fs-4">
                    {payComponents.filter(c => c.component_type === 'Deduction').length}
                  </div>
                  <small className="text-muted">Deductions</small>
                </div>
                <div className="col-md-2">
                  <div className="fw-bold text-info fs-4">
                    {payComponents.filter(c => c.component_type === 'CTC').length}
                  </div>
                  <small className="text-muted">CTC</small>
                </div>
                <div className="col-md-2">
                  <div className="fw-bold text-success fs-4">
                    {payComponents.filter(c => c.is_active).length}
                  </div>
                  <small className="text-muted">Active</small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Navigation Footer */}
      <div className="row mt-4">
        <div className="col-12">
          <div className="card border-0">
            <div className="card-body text-center py-3">
              <div className="btn-group" role="group">
                <button
                  className="btn btn-outline-primary"
                  onClick={goToHome}
                >
                  <i className="bi bi-house me-2"></i>
                  Back to Home
                </button>
                <button
                  className="btn btn-outline-success"
                  onClick={goToDashboard}
                >
                  <i className="bi bi-speedometer2 me-2"></i>
                  Back to Dashboard
                </button>
                <button
                  className="btn btn-outline-info"
                  onClick={goToEmployees}
                >
                  <i className="bi bi-people me-2"></i>
                  Go to Employees
                </button>
                <button
                  className="btn btn-outline-warning"
                  onClick={goToAdminPanel}
                >
                  <i className="bi bi-gear me-2"></i>
                  Go to Admin Panel
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showAddModal && (
        <AddPayComponent
          show={showAddModal}
          onHide={() => setShowAddModal(false)}
          onComponentAdded={fetchPayComponents}
        />
      )}

      {showEditModal && selectedComponent && (
        <EditPayComponent
          show={showEditModal}
          onHide={() => setShowEditModal(false)}
          component={selectedComponent}
          onComponentUpdated={fetchPayComponents}
        />
      )}
    </div>
  );
};

export default PayComponents;